import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import {
	type ChangeEvent,
	type ClipboardEvent,
	type KeyboardEvent,
	type Ref,
	useMemo,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';

import {
	findCountryOptionByIso,
	resolveInstantCountryForCallingCode,
	PHONE_COUNTRY_OPTIONS,
	type PhoneCountryOption
} from '@/utils/formatters/phoneCountryCode';
import {
	formatNationalNumber,
	isCompletePhoneNumber,
	isFreeTextPhoneValue,
	normalizeIddPrefix,
	refineAmbiguousCountryIso,
	sanitizeDigits,
	splitPhoneNumber
} from '@/utils/formatters/phoneNumberParts';

import {
	computeNationalMaxLength,
	filterCountryOptions,
	hideBrokenFlag,
	isPureDigits,
	remapCursorPosition
} from './_utils/phoneNumberInput.utils';
import { phoneNumberInputStyles as sx } from './PhoneNumberInput.styles';

type Props = {
	countryIso: string;
	onCountryIsoChange: (iso: string) => void;
	phoneValue: string;
	onPhoneChange: (value: string) => void;
	disabled?: boolean;
	phoneError?: boolean;
	phoneIsDirty?: boolean;
	phoneId?: string;
	phoneName?: string;
	onFocus?: () => void;
	onBlur?: () => void;
	onKeyDown?: (e: KeyboardEvent) => void;
	onCountryOpenChange?: (open: boolean) => void;
	phoneInputRef?: Ref<HTMLInputElement | null>;
	rootRef?: Ref<HTMLDivElement | null>;
	label?: string;
	placeholder?: string;
	autoFocus?: boolean;
};

export const PhoneNumberInput = ({
	countryIso,
	onCountryIsoChange,
	phoneValue,
	onPhoneChange,
	disabled,
	phoneError,
	phoneIsDirty,
	phoneId,
	phoneName,
	onFocus,
	onBlur,
	onKeyDown,
	onCountryOpenChange,
	phoneInputRef,
	rootRef,
	label,
	placeholder,
	autoFocus
}: Props) => {
	const { t } = useTranslation();
	const selectedCountry = findCountryOptionByIso(countryIso);

	const callingCodeLength = selectedCountry?.callingCode.length ?? 3;
	const nationalMaxLength = computeNationalMaxLength(callingCodeLength);

	const [countryOpen, setCountryOpen] = useState(false);
	const [countryInputValue, setCountryInputValue] = useState('');

	const [failedSelectedFlagIso, setFailedSelectedFlagIso] = useState<
		string | null
	>(null);
	const showSelectedFlag =
		!!selectedCountry && failedSelectedFlagIso !== selectedCountry.iso;
	const filteredCountryOptions = useMemo(
		() => filterCountryOptions(PHONE_COUNTRY_OPTIONS, countryInputValue),
		[countryInputValue]
	);
	const hasFilteredCountryOptions = filteredCountryOptions.length > 0;

	const [emittedPhoneValue, setEmittedPhoneValue] = useState(phoneValue);
	const [displayValue, setDisplayValue] = useState(phoneValue);

	const [completedLockCountry, setCompletedLockCountry] = useState<
		string | null
	>(null);

	if (phoneValue !== emittedPhoneValue) {
		setEmittedPhoneValue(phoneValue);
		setDisplayValue(phoneValue);
		setCompletedLockCountry(null);
	}

	const isFormattingLocked =
		completedLockCountry !== null && completedLockCountry === countryIso;

	const emitPhone = (phone: string) => {
		setEmittedPhoneValue(phone);
		onPhoneChange(phone);
	};

	const applyFormattedValue = (
		e: ChangeEvent<HTMLInputElement>,
		raw: string,
		newValue: string
	) => {
		const cursorPos = e.target.selectionStart ?? raw.length;
		const newCursorPos = remapCursorPosition(raw, newValue, cursorPos);

		e.target.value = newValue;
		e.target.setSelectionRange(newCursorPos, newCursorPos);
		setDisplayValue(newValue);
	};

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const raw = e.target.value;

		if (raw.startsWith('+') || /^00\d/.test(raw)) {
			const parsed = splitPhoneNumber(raw);
			if (parsed.countryIso) {
				onCountryIsoChange(parsed.countryIso);

				if (isPureDigits(parsed.nationalNumber)) {
					const cappedDigits = parsed.nationalNumber.slice(
						0,
						nationalMaxLength
					);
					const formattedPhone = formatNationalNumber(
						parsed.countryIso,
						cappedDigits
					);
					applyFormattedValue(e, raw, formattedPhone);
					emitPhone(formattedPhone);
					return;
				}

				setDisplayValue(raw);
				emitPhone(parsed.nationalNumber);
				return;
			}

			const typedDigits = sanitizeDigits(normalizeIddPrefix(raw));
			const instantMatch = resolveInstantCountryForCallingCode(typedDigits);
			if (instantMatch) {
				const remainingDigits = typedDigits.slice(
					instantMatch.callingCode.length
				);
				onCountryIsoChange(instantMatch.iso);
				setDisplayValue(remainingDigits);
				emitPhone(remainingDigits.slice(0, nationalMaxLength));
				return;
			}

			setDisplayValue(raw);
			emitPhone(raw);
			return;
		}

		const isDeletion = raw.length < displayValue.length;

		if (isFormattingLocked) {
			if (raw.trim() === '') {
				setCompletedLockCountry(null);
				setDisplayValue(raw);
				emitPhone(raw);
				return;
			}

			if (isDeletion) {
				setDisplayValue(raw);
				emitPhone(raw);
				return;
			}

			if (raw.length > nationalMaxLength) {
				e.target.value = displayValue;
				return;
			}

			const formattedPhone = formatNationalNumber(countryIso, raw);
			applyFormattedValue(e, raw, formattedPhone);
			emitPhone(formattedPhone);
			return;
		}

		if (isFreeTextPhoneValue(raw)) {
			if (isDeletion) {
				setDisplayValue(raw);
				emitPhone(raw);
				return;
			}

			if (raw.length > nationalMaxLength) {
				e.target.value = displayValue;
				return;
			}

			const formattedPhone = formatNationalNumber(countryIso, raw);
			applyFormattedValue(e, raw, formattedPhone);
			emitPhone(formattedPhone);
			return;
		}

		const phoneDigits = sanitizeDigits(raw);

		if (phoneDigits.length > nationalMaxLength) {
			e.target.value = displayValue;
			return;
		}

		if (phoneDigits === '' && raw.trim() !== '') {
			setDisplayValue(raw);
			emitPhone(raw);
			return;
		}

		if (phoneDigits !== '' && sanitizeDigits(phoneValue) === phoneDigits) {
			setDisplayValue(raw);
			emitPhone(raw);
			return;
		}

		const resolvedIso = refineAmbiguousCountryIso(countryIso, phoneDigits);
		if (resolvedIso !== countryIso) onCountryIsoChange(resolvedIso);

		const formattedPhone = formatNationalNumber(resolvedIso, phoneDigits);
		emitPhone(formattedPhone);

		if (isCompletePhoneNumber(resolvedIso, phoneDigits)) {
			setCompletedLockCountry(resolvedIso);
		}

		applyFormattedValue(e, raw, formattedPhone);
	};

	const handleCountrySelect = (iso: string) => {
		onCountryIsoChange(iso);
		if (!iso || iso === countryIso || !phoneValue) return;

		const reformatted = formatNationalNumber(iso, phoneValue);
		const newCallingCodeLength =
			findCountryOptionByIso(iso)?.callingCode.length ?? 3;
		const trimmed = reformatted.slice(
			0,
			computeNationalMaxLength(newCallingCodeLength)
		);

		setDisplayValue(trimmed);
		emitPhone(trimmed);
	};

	const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
		const pasted = e.clipboardData.getData('text');
		if (!pasted.trim()) return;

		e.preventDefault();
		const parsed = splitPhoneNumber(pasted);
		if (parsed.countryIso) onCountryIsoChange(parsed.countryIso);

		const nextValue = formatNationalNumber(
			parsed.countryIso || countryIso,
			parsed.nationalNumber.slice(0, nationalMaxLength)
		);

		setDisplayValue(nextValue);
		emitPhone(nextValue);
	};

	return (
		<Box sx={sx.row} ref={rootRef}>
			<Autocomplete
				value={(selectedCountry ?? null) as PhoneCountryOption}
				onChange={(_, option) => handleCountrySelect(option?.iso ?? '')}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={e => onKeyDown?.(e)}
				options={PHONE_COUNTRY_OPTIONS}
				disabled={disabled}
				disableClearable
				autoHighlight
				openText=''
				closeText=''
				open={countryOpen && hasFilteredCountryOptions}
				onOpen={() => {
					setCountryOpen(true);
					onCountryOpenChange?.(true);
				}}
				onClose={() => {
					setCountryOpen(false);
					onCountryOpenChange?.(false);
				}}
				onInputChange={(_, newInputValue) =>
					setCountryInputValue(newInputValue)
				}
				sx={sx.countrySelectRoot(callingCodeLength, showSelectedFlag)}
				getOptionLabel={o => `+${o.callingCode}`}
				isOptionEqualToValue={(o, v) => o.iso === v.iso}
				filterOptions={() => filteredCountryOptions}
				renderOption={(optionProps, option: PhoneCountryOption) => (
					<Box component='li' {...optionProps} key={option.iso} sx={sx.option}>
						<Box
							component='img'
							src={option.flagSrc}
							alt=''
							loading='lazy'
							decoding='async'
							onError={hideBrokenFlag}
							sx={sx.optionFlag}
						/>
						<Box component='span' sx={sx.optionName}>
							{option.name}
						</Box>
						<Box component='span' sx={sx.optionCode}>
							+{option.callingCode}
						</Box>
					</Box>
				)}
				renderInput={params => (
					<TextField
						{...params}
						size='small'
						sx={sx.countrySelect}
						slotProps={{
							...params.slotProps,
							input: {
								...params.slotProps.input,
								startAdornment: showSelectedFlag && (
									<InputAdornment
										position='start'
										sx={sx.selectedFlagAdornment}
									>
										<Box
											component='img'
											src={selectedCountry.flagSrc}
											alt=''
											onError={() =>
												setFailedSelectedFlagIso(selectedCountry.iso)
											}
											sx={sx.selectedFlag}
										/>
									</InputAdornment>
								)
							}
						}}
					/>
				)}
				slotProps={{
					popper: { placement: 'bottom-start', sx: sx.popper }
				}}
			/>

			<TextField
				id={phoneId}
				name={phoneName}
				value={displayValue}
				onChange={handleChange}
				onPaste={handlePaste}
				onFocus={onFocus}
				onBlur={onBlur}
				onKeyDown={onKeyDown}
				inputRef={phoneInputRef}
				// eslint-disable-next-line jsx-a11y/no-autofocus
				autoFocus={autoFocus}
				label={label}
				placeholder={placeholder}
				size='small'
				disabled={disabled}
				error={!!phoneError}
				inputMode='tel'
				sx={sx.phoneInput(!!phoneError, !!phoneIsDirty, !!disabled)}
				slotProps={{
					htmlInput: {
						'aria-label': t('Phone number'),
						maxLength: nationalMaxLength
					}
				}}
			/>
		</Box>
	);
};
